import { webCrypto } from '#web-crypto';
import { calculateBase64Size } from '../utils/base64';
import { arrayToBase64, base64ToArray, concatArrays } from './binary-utils';
import { digestOutput, RSA_ENCRYPTION_ALGO_NAME, RSA_SIGNING_ALGO_NAME, } from './constants';
import { webCryptoTupleToArray } from './web-crypto-tuple';
export const encryptWithRsaBlockCipher = async (data, publicKey) => {
    let base64Encrypted = '';
    const algo = getEncryptionRsaAlgoInfo(publicKey);
    const encryptedBytes = algo.modulusLength / 8;
    const padding = digestOutput[algo.hash.name] / 8;
    const maxBytesToEncrypt = encryptedBytes - 2 * padding - 2;
    let pos = 0;
    const blockArray = [];
    while (pos < data.byteLength) {
        const block = data.slice(pos, Math.min(pos + maxBytesToEncrypt, data.byteLength));
        blockArray.push(block);
        pos += maxBytesToEncrypt;
    }
    const encryptedBlocksPromises = blockArray.map(async (block) => {
        return await webCrypto.subtle.encrypt({
            name: RSA_ENCRYPTION_ALGO_NAME,
        }, publicKey, block);
    });
    const encryptedBlocks = await Promise.all(encryptedBlocksPromises);
    encryptedBlocks.forEach((block) => {
        base64Encrypted += arrayToBase64(block);
    });
    return base64Encrypted;
};
export const decryptWithRsaBlockCipher = async (base64Encrypted, privateKey) => {
    const algo = getEncryptionRsaAlgoInfo(privateKey);
    const blockSize = calculateBase64Size(algo.modulusLength / 8);
    if (base64Encrypted.length % blockSize !== 0) {
        throw new Error(`Encrypted string length must be divisible by ${blockSize}`);
    }
    const blocks = [];
    for (let bs = 0; bs < base64Encrypted.length; bs += blockSize) {
        const block = base64Encrypted.slice(bs, bs + blockSize);
        blocks.push(base64ToArray(block));
    }
    const decryptedBlocksPromises = blocks.map(async (block) => {
        return await webCrypto.subtle.decrypt({
            name: RSA_ENCRYPTION_ALGO_NAME,
        }, privateKey, block);
    });
    const decryptedBlocks = await Promise.all(decryptedBlocksPromises);
    return concatArrays(decryptedBlocks);
};
export const signRsa = async (data, privateKey) => {
    const algorithm = verifySigningRsaAlgoInfo(privateKey);
    const saltBytes = digestOutput[algorithm.hash.name] / 8;
    return await webCrypto.subtle.sign({
        name: RSA_SIGNING_ALGO_NAME,
        saltLength: saltBytes,
    }, privateKey, data);
};
export const verifyRsa = async (data, signature, publicKey) => {
    const algorithm = verifySigningRsaAlgoInfo(publicKey);
    const saltBytes = digestOutput[algorithm.hash.name] / 8;
    return await webCrypto.subtle.verify({
        name: RSA_SIGNING_ALGO_NAME,
        saltLength: saltBytes,
    }, publicKey, signature, data);
};
export const encryptSignRsaBlockCipher = async (data, keys) => {
    const cryptoText = await encryptWithRsaBlockCipher(data, keys.encryptPair.publicKey);
    const signature = await signRsa(await webCryptoTupleToArray([cryptoText]), keys.signPair.privateKey);
    return `${cryptoText}:${arrayToBase64(signature)}`;
};
export const verifyDecryptRsaBlockCipher = async (data, keys) => {
    const [cryptoText, signature, ...rest] = data.split(':');
    if (!cryptoText || !signature || rest.length) {
        throw new Error('Bad signed encrypted value');
    }
    const isValid = await verifyRsa(await webCryptoTupleToArray([cryptoText]), base64ToArray(signature), keys.signPair.publicKey);
    if (!isValid) {
        return null;
    }
    return decryptWithRsaBlockCipher(cryptoText, keys.encryptPair.privateKey);
};
const getEncryptionRsaAlgoInfo = (cryptoKey) => {
    if (cryptoKey.algorithm.name !== RSA_ENCRYPTION_ALGO_NAME) {
        throw new Error(`Expected algorithm is ${RSA_ENCRYPTION_ALGO_NAME}. Given is ${cryptoKey.algorithm.name}`);
    }
    return cryptoKey.algorithm;
};
const verifySigningRsaAlgoInfo = (cryptoKey) => {
    if (cryptoKey.algorithm.name !== RSA_SIGNING_ALGO_NAME) {
        throw new Error(`Expected algorithm is ${RSA_SIGNING_ALGO_NAME}. Given is ${cryptoKey.algorithm.name}`);
    }
    return cryptoKey.algorithm;
};
